import { ARCHER_APP_NAME } from '../constants.js';
/**
 * Talks to the Omniapps Account service for auth + user info.
 * Stores tokens in any TokenStore implementation (localStorage / IndexedDB / in-memory).
 */
export class AccountClient {
    store;
    baseUrl;
    appName;
    constructor(store, options = {}) {
        this.store = store;
        this.baseUrl = options.baseUrl ?? 'https://account.omniapps.net';
        this.appName = options.appName ?? ARCHER_APP_NAME;
    }
    getTokens() {
        return this.store.read();
    }
    async login(email, password) {
        const res = await fetch(`${this.baseUrl}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        });
        if (!res.ok) {
            const err = (await res.json().catch(() => ({})));
            throw new Error(err.message ?? err.error ?? `login failed: ${res.status}`);
        }
        const data = (await res.json());
        this.persist(data.accessToken, data.refreshToken, 15 * 60);
        return data.user ?? (await this.me());
    }
    async register(payload) {
        const res = await fetch(`${this.baseUrl}/api/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!res.ok) {
            const err = (await res.json().catch(() => ({})));
            throw new Error(err.message ?? err.error ?? `register failed: ${res.status}`);
        }
        return (await this.login(payload.email, payload.password));
    }
    async logout() {
        const tokens = this.store.read();
        if (!tokens)
            return;
        try {
            await fetch(`${this.baseUrl}/api/auth/logout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken: tokens.refreshToken }),
            });
        }
        finally {
            this.store.clear();
        }
    }
    async me() {
        const user = await this.fetchMe();
        return user;
    }
    async requestPasswordReset(email) {
        await fetch(`${this.baseUrl}/api/auth/request-password-reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });
    }
    /**
     * Exchange an API key for a short-lived access token bound to `appName`.
     * Used internally for AI features when we need a token without going through the user login.
     */
    async exchangeApiKey(apiKey) {
        const res = await fetch(`${this.baseUrl}/api/keys/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ apiKey }),
        });
        if (!res.ok)
            throw new Error(`api key exchange failed: ${res.status}`);
        return (await res.json());
    }
    async getValidAccessToken() {
        const tokens = this.store.read();
        if (!tokens)
            return null;
        if (Date.now() < tokens.expiresAt - 30_000)
            return tokens.accessToken;
        const refreshed = await this.refresh(tokens.refreshToken);
        this.persist(refreshed.accessToken, refreshed.refreshToken, refreshed.expiresIn);
        return refreshed.accessToken;
    }
    async refresh(refreshToken) {
        const res = await fetch(`${this.baseUrl}/api/auth/refresh-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
        });
        if (!res.ok)
            throw new Error(`token refresh failed: ${res.status}`);
        const data = (await res.json());
        return data;
    }
    async fetchMe() {
        const token = await this.getValidAccessToken();
        if (!token)
            throw new Error('not authenticated');
        const res = await fetch(`${this.baseUrl}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
            const tokens = this.store.read();
            if (tokens) {
                const refreshed = await this.refresh(tokens.refreshToken);
                this.persist(refreshed.accessToken, refreshed.refreshToken, refreshed.expiresIn);
                return this.fetchMe();
            }
        }
        if (!res.ok)
            throw new Error(`account /me failed: ${res.status}`);
        const data = (await res.json());
        return data.user;
    }
    persist(accessToken, refreshToken, expiresInSeconds) {
        this.store.write({
            accessToken,
            refreshToken,
            expiresAt: Date.now() + expiresInSeconds * 1000,
        });
    }
    /** exposes appName for client code that needs it (e.g. data-key namespaces) */
    getAppName() {
        return this.appName;
    }
}
/** Persists tokens in localStorage under a single key. */
export class LocalStorageTokenStore {
    key;
    constructor(key = 'archer.auth.tokens') {
        this.key = key;
    }
    read() {
        if (typeof localStorage === 'undefined')
            return null;
        const raw = localStorage.getItem(this.key);
        if (!raw)
            return null;
        try {
            return JSON.parse(raw);
        }
        catch {
            return null;
        }
    }
    write(tokens) {
        if (typeof localStorage === 'undefined')
            return;
        localStorage.setItem(this.key, JSON.stringify(tokens));
    }
    clear() {
        if (typeof localStorage === 'undefined')
            return;
        localStorage.removeItem(this.key);
    }
}
/** In-memory token store, useful for tests. */
export class MemoryTokenStore {
    current = null;
    read() {
        return this.current;
    }
    write(tokens) {
        this.current = tokens;
    }
    clear() {
        this.current = null;
    }
}
//# sourceMappingURL=index.js.map